import 'dayjs/locale/da';
import { DatesProvider } from '@mantine/dates';
import dayjs from 'dayjs';
import { createContext, useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { LOCALES, isLocale, translate } from './core.js';
import type { Catalogue, Locale, Vars } from './core.js';
import { da } from './da.js';
import { en } from './en.js';
import type { MessageKey } from './da.js';

/**
 * Which language the application is being read in.
 *
 * ## Where the preference actually lives
 *
 * Two places, deliberately, because they answer different questions:
 *
 * - **`users.locale`**, through `PATCH /v1/users/me` — the durable answer. It
 *   follows the person to another browser, which is what a preference on Min
 *   konto is for.
 * - **`localStorage`** — the immediate one. The profile endpoint needs a
 *   *company* token, which does not exist on the login screen or during the
 *   first render after a reload. Reading the server first would mean the app
 *   opens in Danish and flips to English a moment later, on every load.
 *
 * So localStorage is read synchronously at start-up and the server value is
 * applied once the profile arrives. Changing the language writes both.
 *
 * `da` is the fallback everywhere: it is the language the application was
 * written in, and the one its books are kept in.
 */

export interface I18nValue {
  readonly locale: Locale;
  readonly t: (key: MessageKey, vars?: Vars) => string;
  /** Set for this browser. Persisting to the account is the caller's job. */
  readonly setLocale: (locale: Locale) => void;
}

export const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = 'accounting.locale';

/*
  Typed by the key union rather than by `typeof da`. The Danish catalogue is
  `as const`, so its type is a record of *literal* strings -- English is not
  assignable to that, and should not be.
*/
const CATALOGUES: Readonly<Record<Locale, Catalogue<MessageKey>>> = { da, en };

/**
 * The locale to open in, before anything has been fetched.
 *
 * What this browser last chose, and otherwise **Danish** — deliberately, and
 * not `navigator.language`.
 *
 * Sniffing the browser is the conventional thing and would be wrong here. This
 * is a Danish accounting system whose books, chart of accounts and statutory
 * exports are Danish, and its users run Windows and browsers in English as
 * often as not. Auto-detecting would have greeted the people it was built for
 * with a language they did not ask for, the first time they loaded it after
 * this shipped.
 *
 * English is a choice someone makes, and it then follows them: the switcher
 * writes it here and to `users.locale`, so the next browser starts English too.
 */
export function initialLocale(): Locale {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored !== null && stored !== undefined && isLocale(stored)) {
      return stored;
    }
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    // A remembered language is a convenience, never a requirement.
  }
  return 'da';
}

function remember(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // As above.
  }
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    remember(next);
    /*
      The document's own language, for anything that is not React: a screen
      reader choosing a voice, the browser offering to translate, and CSS
      `:lang()` if it is ever needed. Missing this is the classic i18n bug that
      nobody sees because it is not visible.
    */
    if (globalThis.document !== undefined) {
      globalThis.document.documentElement.lang = next;
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const catalogue = CATALOGUES[locale];
    return {
      locale,
      t: (key: MessageKey, vars?: Vars) => translate(catalogue, key, vars),
      setLocale,
    };
  }, [locale, setLocale]);

  /*
    dayjs drives Mantine's date pickers, and its default locale is English —
    so a Danish application has been showing "January" in its date inputs all
    along. It is a module-level global rather than a React value, hence the
    assignment here rather than a prop.
  */
  dayjs.locale(locale === 'da' ? 'da' : 'en');

  return (
    <I18nContext.Provider value={value}>
      <DatesProvider settings={{ locale, firstDayOfWeek: 1, weekendDays: [0, 6] }}>
        {children}
      </DatesProvider>
    </I18nContext.Provider>
  );
}

export { LOCALES };
export type { Locale, MessageKey };
